import config from 'config'
import env from 'env'

import { routes } from './routes.js'

import { CJEVENT_ID_COOKIE } from './constants'
import { isEmailAddressInWhitelistedDomains } from 'core/services/ProfileService'
import App from './App.vue'
import DatePlugin from 'core/plugins/DatePlugin.js'

import FullStoryPlugin from 'core/external-plugins/FullStoryPlugin.js'
import GeographyUtil from 'core/utilities/GeographyUtil'
import GoogleTagManagerPlugin from 'core/external-plugins/GoogleTagManagerPlugin.js'
import highlightsJson from './highlights.json'
import promotedEventsJson from './promoted-events.json'
import highlightsIntegratedJson from './highlights-integrated.json'
import MobileDetect from 'mobile-detect'
import Notifications from 'vue-notification'
import NumeralPlugin from 'core/plugins/NumeralPlugin.js'
import PhoneUtil from 'core/utilities/PhoneUtil.js'

// Integrated & Non-Integrated specific plugins
import MarketplacePlugin from 'core/plugins/MarketplacePlugin.js'
import PartnershipsPlugin from '../src/PartnershipsPlugin.js'
import GeographyUtilIntegrated from '../src/GeographyUtilIntegrated.js'
import PartnershipsStockTypePlugin from '../src/PartnershipsStockTypePlugin.js'
import StockTypePlugin from 'core/plugins/StockTypePlugin.js'

import queryString from 'query-string'
import StorageUtil from 'core/utilities/StorageUtil.js'
import Timer from 'core/utilities/TimerUtil.js'
import VeeValidate, { Validator } from 'vee-validate'
import {
    getConfigErrors,
    isDefaultToRedeemingLoyaltyPoints
} from '../src/services/ConfigService'
import { updateRecentSearches } from '../src/services/EntityService'
import Vue from 'vue'
import VueHead from 'vue-head'
import VueMask from 'v-mask'
import VueResource from 'vue-resource'
import VueRouter from 'vue-router'
import Vuex from 'vuex'
import vuexI18n from 'vuex-i18n'
import WindowPlugin from './plugins/Window.js'
import ZendeskChat from './plugins/ZendeskChat.js'
import VueVirtualScroller from 'vue-virtual-scroller'
import 'vue-virtual-scroller/dist/vue-virtual-scroller.css'

const apiBasePath = /* eslint-disable no-undef */API_BASE_PATH/* eslint-enable no-undef */
const isProduction = /* eslint-disable no-undef */IS_PRODUCTION/* eslint-enable no-undef */
const partnerGA = (isProduction ? config.gaIdProd : config.gaIdStaging) || null
const partnerGtmContainerId = isProduction ? config.gtmContainerIdProd : config.gtmContainerIdStaging
const gtmContainerIds = [env.GTM_UMBRELLA_CONTAINER_ID, partnerGtmContainerId].filter(containerId => Boolean(containerId))
let inactivityTimer = null

if (gtmContainerIds.length > 0) {
    Vue.use(GoogleTagManagerPlugin, {
        containerIds: [env.GTM_UMBRELLA_CONTAINER_ID, partnerGtmContainerId].filter(containerId => Boolean(containerId)),
        partnerGA: partnerGA
    })
}

Vue.use(DatePlugin)
Vue.use(Notifications)
Vue.use(NumeralPlugin)
Vue.use(VueHead, {
    separator: '',
    complement: ''
})
Vue.use(VueMask)
Vue.use(VueResource)
Vue.use(VueRouter)
Vue.use(Vuex)
Vue.use(WindowPlugin)
Vue.use(ZendeskChat)
Vue.use(VueVirtualScroller)

if (env.HAS_FULLSTORY) {
    Vue.use(FullStoryPlugin)
}

const configValue = config.isIntegrated ? 'integrated' : 'notIntegrated'
const apiConfig = {
    integrated: {
        geographyUtil: GeographyUtilIntegrated,
        highlights: highlightsIntegratedJson,
        plugin: PartnershipsPlugin,
        stock_type_plugin: PartnershipsStockTypePlugin,
        basePath: apiBasePath || '/partnership-api',
        partnerHeader: 'PARTNER-ID',
        searchPerPage: 25
    },
    notIntegrated: {
        geographyUtil: GeographyUtil,
        highlights: highlightsJson,
        promoted_events: promotedEventsJson,
        plugin: MarketplacePlugin,
        stock_type_plugin: StockTypePlugin,
        basePath: apiBasePath || '/marketplace-api',
        partnerHeader: 'X-AFFILIATE-ID',
        searchPerPage: 10
    }
}[configValue]

Vue.prototype.$config = Object.assign({}, config, apiConfig)

const configErrors = getConfigErrors(config)
if (configErrors.length) {
    throw new Error(configErrors[0].message)
}

Vue.http.headers.common[apiConfig.partnerHeader] = String(config.affiliateId)

let partnerId = config.affiliateId
if (config.isIntegrated && config.wsuserId) {
    partnerId = config.wsuserId
}
Vue.http.headers.common[apiConfig.partnerHeader] = String(partnerId)

if (config.loyalty) {
    Vue.http.headers.common['X-LOYALTY-PROGRAM-NAME'] = config.loyalty.programName
}

Vue.http.headers.common['X-CHANNEL'] = 'Web'

Vue.use(apiConfig.stock_type_plugin)
Vue.use(apiConfig.plugin, { basePath: apiConfig.basePath })

const store = new Vuex.Store({
    actions: {
        initSession (context, authResult) {
            const session = {
                accountId: authResult.userProfile.id,
                token: authResult.token
            }

            const timeoutSeconds = config.auth.inactivityTimeoutThreshold || env.INACTIVE_TIMEOUT_SECONDS

            StorageUtil.cookie.set('session', JSON.stringify(session), { secure: true })

            if (config.loyalty) {
                context.commit('setLoyalty', { ...store.state.loyalty, ...authResult.loyalty })
            }
            context.commit('setProgram', authResult.program)
            context.commit('setSessionToken', authResult.token)
            context.commit('setUserProfile', authResult.userProfile)

            if (!config.auth.hasAutoAuth) {
                Vue.http.headers.common.Authorization = `Bearer ${authResult.token}`
            }

            if (config.auth.hasInactivityTimeout && authResult.program && authResult.program.logout_url) {
                inactivityTimer = new Timer(timeoutSeconds)

                inactivityTimer.once('end', () => context.dispatch('logout', { isTimedout: true }))
                inactivityTimer.on('reset', () => {
                    if (context.state.program && context.state.program.keep_alive_url) {
                        Vue.http.get(context.state.program.keep_alive_url).catch(err => console.error(err))
                    }
                })

                inactivityTimer.start()
            }

            if (context.state.program && context.state.program.keep_alive_url && context.state.program.keep_alive_interval && Number.isInteger(context.state.program.keep_alive_interval)) {
                const intervalId = setInterval(() => {
                    if (context.state.program) {
                        Vue.http
                            .get(context.state.program.keep_alive_url)
                            .catch(err => console.error(err))
                    } else {
                        clearInterval(intervalId)
                    }
                }, context.state.program.keep_alive_interval * 1000)
            }

            Vue.http.interceptors.push((request) => {
                if (request.url.indexOf('/marketplace-api') !== 0) {
                    return
                }
                if (inactivityTimer && inactivityTimer.isStarted) {
                    inactivityTimer.reset()
                }
            })

            Vue.http.interceptors.push((_request, next) => {
                next(response => {
                    if (response.status === 401) {
                        context.dispatch('logout', { isTimedoutOnServer: true })
                    }
                })
            })

            return authResult
        },
        loginConcierge (context, {
            username,
            password
        }) {
            return Vue.prototype.$marketplace
                .login(username, password)
                .then(response => {
                    return {
                        loyalty: null,
                        program: null,
                        token: response.token,
                        userProfile: response.account
                    }
                })
                .then(authResult => {
                    context.dispatch('initSession', authResult)
                })
        },
        loginLoyalty (context, {
            username,
            password
        }) {
            if (!config.loyalty.programName) {
                throw new Error('config.loyalty.programName is required.')
            }
            return Vue.http
                .post(`/marketplace-api/v2/sso/login/${config.loyalty.programName}`, {
                    username,
                    password
                })
                .then(response => {
                    return {
                        loyalty: response.body.loyalty,
                        program: response.body.program,
                        token: response.body.token,
                        userProfile: response.body.account
                    }
                })
                .then(authResult => {
                    context.dispatch('initSession', authResult)
                })
                .catch(response => {
                    if (response instanceof Error) {
                        throw response
                    }

                    if (response.body) {
                        return Promise.reject(response.body.errors)
                    } else {
                        /* eslint-disable prefer-promise-reject-errors */
                        return Promise.reject({
                            code: 'network-error',
                            message: ''
                        })
                        /* eslint-enable prefer-promise-reject-errors */
                    }
                })
        },
        logout (context, {
            isTimedoutOnClient = false,
            isTimedoutOnServer = false
        } = {}) {
            let programLogoutUrl = null
            let programTimedoutUrl = null

            if (context.state.program && context.state.program.home_url) {
                programLogoutUrl = context.state.program.home_url
            }

            if (context.state.program && context.state.program.logout_url) {
                programTimedoutUrl = context.state.program.logout_url
            }

            if (!isTimedoutOnServer && config.auth && config.auth.isDeleteApiSessionOnLogout) {
                Vue.http
                    .post(`/marketplace-api/v2/sso/logout/${config.shortName}/${context.state.userProfile.id}`, {
                        token: context.state.sessionToken
                    })
                    .catch(() => null)
            }

            context.commit('setLoyalty', null)
            context.commit('setProgram', null)
            context.commit('setSessionToken', null)
            context.commit('setUserProfile', null)

            delete Vue.http.headers.common.Authorization

            StorageUtil.cookie.remove('session')

            if (programLogoutUrl) {
                router.replace({ name: 'logout' })
                location.href = programLogoutUrl
            } else if ((isTimedoutOnClient || isTimedoutOnServer) && programTimedoutUrl) {
                router.replace({ name: 'unauthorized' })
                location.href = programTimedoutUrl
            } else if (config.logoutUrl) {
                router.replace({ name: 'logout' })
                location.href = config.logoutUrl
            } else if (config.auth && config.auth.isConcierge) {
                router.replace({ name: 'login' })
            } else {
                router.replace({ name: 'logout' })
            }
        }
    },
    strict: true,
    state: {
        cjEventId: null,
        event: null,
        hasShownQuebecUserModal: false,
        imagesCdn: env.STATIC_ASSETS_URL + /* eslint-disable no-undef */STATIC_ASSETS_DIR/* eslint-enable no-undef */ + '/images',
        isMobile: Boolean(new MobileDetect(window.navigator.userAgent).mobile()),
        isRedeemingLoyaltyPoints: isDefaultToRedeemingLoyaltyPoints(config),
        lastEventsPath: null,
        lastViewedEventId: null,
        loyalty: null,
        multiStepTicket: null,
        program: null,
        purchaseTicket: null,
        recentEventIds: [],
        recentSearches: [],
        selectedLocation: null,
        sessionId: null,
        sessionToken: null,
        ticketOrder: null,
        userLocationPromise: null,
        userProfile: null,
        selectedDeliveryOption: null
    },
    modules: {
        i18n: vuexI18n.store
    },
    mutations: {
        addRecentSearch (state, search) {
            state.recentSearches = updateRecentSearches(state.recentSearches, search)
        },
        removeRecentSearchAt (state, index) {
            state.recentSearches = state.recentSearches.slice(0, index).concat(state.recentSearches.slice(index + 1))
        },
        setRecentSearches (state, recentSearches) {
            state.recentSearches = recentSearches
        },
        addRecentEventId (state, eventId) {
            const duplicateIndex = state.recentEventIds.findIndex(nextEventId => {
                return nextEventId === eventId
            })

            if (duplicateIndex > -1) {
                state.recentEventIds = [eventId]
                    .concat(
                        state.recentEventIds.slice(0, duplicateIndex),
                        state.recentEventIds.slice(duplicateIndex + 1)
                    )
                    .slice(0, 3)
            } else {
                state.recentEventIds = [eventId]
                    .concat(state.recentEventIds)
                    .slice(0, 3)
            }
        },
        removeRecentEventIdAt (state, index) {
            state.recentEventIds = state.recentEventIds.slice(0, index).concat(state.recentEventIds.slice(index + 1))
        },
        setEvent (state, event) {
            state.event = event
        },
        setIsRedeemingLoyaltyPoints (state, isRedeemingLoyaltyPoints) {
            state.isRedeemingLoyaltyPoints = isRedeemingLoyaltyPoints
        },
        setLastViewedEventId (state, eventId) {
            state.lastViewedEventId = eventId
        },
        setLastEventsPath (state, path) {
            state.lastEventsPath = path
        },
        setLoyalty (state, loyalty) {
            state.loyalty = loyalty
        },
        setOrderConfirmation (state, orderConfirmation) {
            state.orderConfirmation = orderConfirmation
        },
        setProgram (state, program) {
            state.program = program
        },
        setPurchaseTicket (state, purchaseTicket) {
            state.purchaseTicket = purchaseTicket
        },
        setMultiStepTicket (state, multiStepTicket) {
            state.multiStepTicket = multiStepTicket
        },
        setRecentEventIds (state, recentEventIds) {
            state.recentEventIds = recentEventIds
        },
        setSelectedLocation (state, selectedLocation) {
            state.selectedLocation = selectedLocation
        },
        setSessionId (state, sessionId) {
            state.sessionId = sessionId
        },
        setSessionToken (state, sessionToken) {
            state.sessionToken = sessionToken
        },
        setTicketOrder (state, ticketOrder) {
            state.ticketOrder = ticketOrder
        },
        setUserLocationPromise (state, userLocationPromise) {
            state.userLocationPromise = userLocationPromise
        },
        setUserProfile (state, userProfile) {
            state.userProfile = userProfile
        },
        setCjEventId (state, cjEventId) {
            state.cjEventId = cjEventId
        },
        setStrikethroughMinPriceThreshold (state, strikethroughMinPriceThreshold) {
            state.strikethroughMinPriceThreshold = strikethroughMinPriceThreshold
        },
        setStrikethoughDiscountThreshold (state, strikethoughDiscountThreshold) {
            state.strikethoughDiscountThreshold = strikethoughDiscountThreshold
        },
        setHasShownQuebecUserModal (state, value) {
            state.hasShownQuebecUserModal = value
        },
        setDelivery (state, value) {
            state.selectedDeliveryOption = value
        }
    }
})

store.commit('setIsRedeemingLoyaltyPoints', StorageUtil.session.get('is-redeeming-loyalty-points') || store.state.isRedeemingLoyaltyPoints)
store.watch(state => state.isRedeemingLoyaltyPoints, isRedeemingLoyaltyPoints => {
    StorageUtil.session.set('is-redeeming-loyalty-points', isRedeemingLoyaltyPoints)
})

store.commit('setCjEventId', StorageUtil.cookie.get(CJEVENT_ID_COOKIE) || store.state.cjEventId)
store.watch(state => state.cjEventId, cjEventId => {
    StorageUtil.cookie.set(CJEVENT_ID_COOKIE, cjEventId, {
        expires: 30,
        secure: true
    })
})

store.commit('setSessionId', StorageUtil.session.get('session-id') || store.state.sessionId)
store.watch(state => state.sessionId, sessionId => {
    StorageUtil.session.set('session-id', sessionId)
})

store.commit('setPurchaseTicket', StorageUtil.session.get('purchase-ticket') || store.state.purchaseTicket)
store.watch(state => state.purchaseTicket, purchaseTicket => {
    StorageUtil.session.set('purchase-ticket', purchaseTicket)
})

store.commit('setMultiStepTicket', StorageUtil.session.get('multi-step-ticket') || store.state.multiStepTicket)
store.watch(state => state.multiStepTicket, multiStepTicket => {
    StorageUtil.session.set('multi-step-ticket', multiStepTicket)
})

store.commit('setTicketOrder', StorageUtil.session.get('ticket-order') || store.state.ticketOrder)
store.watch(state => state.ticketOrder, ticketOrder => {
    StorageUtil.session.set('ticket-order', ticketOrder)
})

store.commit('setRecentSearches', StorageUtil.local.get('recent-searches') || [])
store.watch(state => state.recentSearches, recentSearches => {
    StorageUtil.local.set('recent-searches', recentSearches)
})

store.commit('setRecentEventIds', StorageUtil.local.get('recent-events-ids') || [])
store.watch(state => state.recentEventIds, recentEventIds => {
    StorageUtil.local.set('recent-events-ids', recentEventIds)
})

store.commit('setSelectedLocation', StorageUtil.session.get('selected-location') || store.state.selectedLocation)
store.watch(state => state.selectedLocation, selectedLocation => {
    StorageUtil.session.set('selected-location', selectedLocation)
})

store.watch(state => state.userProfile, userProfile => {
    if (!(userProfile && typeof (Vue.prototype.$fullStory || {}).setUserVars === 'function')) {
        return
    }

    Vue.prototype.$fullStory.setUserVars({
        displayName: userProfile.first_name,
        email: userProfile.email_address
    })
})

const userLocationPromise = Vue.prototype.$marketplace
    .getLocation(null, { timeout: 3000 })
    .then(location => location)
    .catch(_err => null) // There is no need to handle 4xx server responses.

Validator.extend('phone', {
    getMessage: field => `The ${field} field is invalid.`,
    validate: PhoneUtil.isPhoneValid
})

Validator.extend('whitelisted-domain', {
    getMessage: () => 'This field is invalid.',
    validate: value => {
        if (!((config.auth || {}).registrationEmailDomainWhitelist || []).length) {
            return true
        }
        return isEmailAddressInWhitelistedDomains(value, config.auth.registrationEmailDomainWhitelist)
    }
})

Validator.extend('not-cc', {
    getMessage: field => {
        const label = document.querySelector('label[for=' + field + ']')
        const placeholder = label ? label.innerHTML : 'value'
        return `please enter a valid ${placeholder}`
    },
    validate: value => {
        if (/[^0-9-\s]+/.test(value)) {
            return true
        }

        const num = value.replace(/[^0-9]/g, '')

        if (num.length >= 15) {
            let nCheck = 0
            let bEven = false
            value = value.replace(/\D/g, '')
            for (let n = value.length - 1; n >= 0; n--) {
                const cDigit = value.charAt(n)
                let nDigit = parseInt(cDigit, 10)
                if (bEven && (nDigit *= 2) > 9) {
                    nDigit -= 9
                }
                nCheck += nDigit
                bEven = !bEven
            }
            const isCC = (nCheck % 10) === 0
            return !isCC
        }
        return true
    }
})

Validator.localize({
    en: {
        custom: {
            MultiStepContactEmail: {
                required: 'Please enter your email address to continue.'
            },
            RegisterConfirmPassword: {
                confirmed: 'Passwords do not match.'
            },
            RegisterPassword: {
                min: 'This field must be at least 8 characters.'
            },
            UpdatePasswordConfirm: {
                confirmed: 'Passwords do not match.'
            },
            UpdatePasswordNew: {
                min: 'This field must be at least 8 characters.'
            }
        },
        messages: {
            required: 'This field is required.',
            numeric: 'This field must be a number.',
            phone: 'Phone Number must be valid.',
            credit_card: 'Credit Card Number must be valid.',
            email: 'Email Address must be valid.'
        }
    }
})

Vue.use(VeeValidate)

Vue.use(vuexI18n.plugin, store)
Vue.i18n.set('en')

Vue.prototype.$marketplace.getConfig()
    .then(response => {
        if (response.rules.strikethrough_min_price_threshold) {
            store.commit('setStrikethroughMinPriceThreshold', response.rules.strikethrough_min_price_threshold)
        }
        if (response.rules.strikethrough_discount_threshold) {
            store.commit('setStrikethoughDiscountThreshold', response.rules.strikethrough_discount_threshold)
        }
    })
    .catch(error => {
        console.error(error)
    })

if (config.loyalty) {
    const loyaltyDefault = {
        currency_formatter: config.loyalty.currencyFormatter || null,
        currency_label: config.loyalty.currencyLabel || null,
        points_round_to: config.loyalty.pointsRoundTo || null
    }
    store.commit('setLoyalty', loyaltyDefault)
}
store.commit('setUserLocationPromise', userLocationPromise)

const authenticate = (partnerConfig, queryAuthToken) => {
    if (!partnerConfig.auth) {
        return Promise.resolve(null)
    }

    const partnerShortName = partnerConfig.shortName
    if (!partnerShortName) {
        throw new Error('shortName config is required')
    }

    const existingSession = JSON.parse(StorageUtil.cookie.get('session'))

    if (partnerConfig.auth.hasAutoAuth) {
        const accountId = queryAuthToken || (existingSession || {}).accountId || null
        if (!accountId) {
            return Promise.resolve(null)
        }

        return Promise.resolve({
            loyalty: {},
            program: {},
            token: accountId,
            userProfile: {
                id: accountId
            }
        })
    } else if (queryAuthToken) {
        Vue.http.headers.common.Authorization = `Bearer ${queryAuthToken}`
        let initVariable = partnerShortName
        if (config.isIntegrated) {
            initVariable = config.wsuserId
        }
        return Vue.prototype.$marketplace
            .initSession(initVariable)
            .then(response => {
                if (!response.success) {
                    return Promise.reject(new Error('Invalid response'))
                }
                return {
                    loyalty: response.loyalty,
                    program: response.program,
                    token: response.token,
                    userProfile: response.account
                }
            })
            .catch(() => {
            })
            .finally(() => {
                delete Vue.http.headers.common.Authorization
            })
    } else if (existingSession) {
        Vue.http.headers.common.Authorization = `Bearer ${existingSession.token}`
        return Vue.prototype.$marketplace
            .getProfile(existingSession.accountId)
            .then(response => {
                return {
                    loyalty: response.loyalty,
                    program: response.program,
                    token: existingSession.token,
                    userProfile: response.account
                }
            })
            .catch(() => null)
            .finally(() => {
                delete Vue.http.headers.common.Authorization
            })
    } else {
        return Promise.resolve(null)
    }
}

const router = new VueRouter({
    mode: 'history',
    routes,
    scrollBehavior (to, from, savedPosition) {
        if (savedPosition) {
            // savedPosition is only available when user navigates with back/forward
            return savedPosition
        } else if (to.name === from.name) {
            // Use current scroll position when url changes but the route is the same
            // example: query param change

        } else {
            // Reset scroll position
            return {
                x: 0,
                y: 0
            }
        }
    }
})

router.beforeEach((to, from, next) => {
    const isAuthRequired = Boolean(config.auth) && !to.meta.isPublic
    const isConcierge = Boolean(config.auth && config.auth.isConcierge)
    const isShowLoginModal = Boolean(config.auth && config.auth.hasLoginModal)
    const isDisabled = Boolean(config.disabled || false)
    let loginUrl = config.auth ? config.auth.loginUrl : null

    if (inactivityTimer && inactivityTimer.isStarted) {
        inactivityTimer.reset()
    }

    if (to.meta.hasEvents) {
        store.commit('setLastEventsPath', to.fullPath)
    }

    if (isDisabled) {
        if (to.name === 'help' || to.name === 'privacy' || to.name === 'unauthorized') {
            next()
        } else {
            next({
                name: 'unauthorized',
                replace: true
            })
        }
    } else if (isAuthRequired && !store.state.userProfile) {
        if (isConcierge) {
            next({
                name: 'login',
                query: Object.assign({}, from.query, {
                    continue: to.fullPath
                })
            })
        } else if (isShowLoginModal && from.meta.isPublic) {
            next({
                path: from.path,
                query: Object.assign({}, from.query, {
                    continue: to.fullPath,
                    modal: 'login'
                })
            })
        } else if (loginUrl) {
            loginUrl = loginUrl.replace('{{RETURN_URL}}', encodeURIComponent(to.fullPath))
            location.href = loginUrl
        } else {
            next({
                name: 'unauthorized',
                replace: true
            })
        }
    } else if (to.query.token) {
        const query = { ...to.query }
        delete query.token

        next({
            path: to.path,
            replace: true,
            query
        })
    } else if (to.query.cjevent) {
        const query = { ...to.query }
        const cjEventId = query.cjevent
        store.commit('setCjEventId', cjEventId)

        delete query.cjevent

        next({
            path: to.path,
            replace: true,
            query
        })
    } else if (to.query.lat === null && to.query.lon === null) {
        next({
            path: to.path
        })
    } else {
        next()
    }
})

router.afterEach((to, from) => {
    if (Vue.prototype.$zendeskChat) {
        if (to.meta.hasLiveChat) {
            Vue.prototype.$zendeskChat.show()
        } else {
            Vue.prototype.$zendeskChat.hide()
        }
    }
})

authenticate(config, queryString.parse(location.search).token)
    .then(authResult => {
        if (authResult) {
            store.dispatch('initSession', authResult)
        }

        window.vsApp = new Vue({
            el: '#App',
            render: (h) => h(App),
            router,
            store
        })
    })
